import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Button,
  Typography,
  Tag,
  Popconfirm,
  message,
} from "antd";
import { BenchmarkResult } from "@/lib/api/resources/benchmark";
import { ReferenceViewer } from "./ReferenceViewer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  promoteBenchmarkRun,
  deleteBenchmarkRunGroundTruth,
} from "@/lib/api/resources/benchmark";

const { Text } = Typography;

interface PromoteBenchmarkRunModalProps {
  runId: string;
  results: BenchmarkResult[];
  isOpen: boolean;
  onClose: () => void;
}

const formatOutput = (output: any): string => {
  if (!output || typeof output !== "object") return "—";
  if (output.error) return `Error: ${output.error}`;
  if (Array.isArray(output.functionCalls) && output.functionCalls.length > 0) {
    return JSON.stringify(
      output.functionCalls[0]?.arguments ?? output.functionCalls[0],
    );
  }
  if (typeof output.text === "string" && Object.keys(output).length === 1)
    return output.text || "—";
  const values = Object.values(output);
  return values.length === 1 ? String(values[0]) : JSON.stringify(output);
};

export const PromoteBenchmarkRunModal: React.FC<
  PromoteBenchmarkRunModalProps
> = ({ runId, results, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Pre-select items with quality score >= 8
  useEffect(() => {
    if (isOpen) {
      const defaultSelected = results
        .filter(
          (r) =>
            r.qualityScore != null &&
            r.qualityScore >= 8 &&
            !(r.actualOutput && (r.actualOutput as any).error),
        )
        .map((r) => r.id);
      setSelectedRowKeys(defaultSelected);
    } else {
      setSelectedRowKeys([]);
    }
  }, [isOpen, results]);

  const promoteMutation = useMutation({
    mutationFn: (resultIds: string[]) => promoteBenchmarkRun(runId, resultIds),
    onSuccess: (data) => {
      message.success(
        `Promoted! Overwrote ground truth with ${data.promotedCount} outputs.`,
      );
      queryClient.invalidateQueries({ queryKey: ["benchmarkRuns"] });
      queryClient.invalidateQueries({ queryKey: ["benchmark_run", runId] });
      onClose();
    },
    onError: (error: any) => {
      message.error(error.message || "Failed to promote run");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBenchmarkRunGroundTruth(runId),
    onSuccess: () => {
      message.success("Ground truth cleared successfully.");
      queryClient.invalidateQueries({ queryKey: ["benchmarkRuns"] });
      queryClient.invalidateQueries({ queryKey: ["benchmark_run", runId] });
      onClose();
    },
    onError: (error: any) => {
      message.error(
        error.message || "Failed to clear ground truth or it does not exist.",
      );
    },
  });

  const columns = [
    {
      title: "Input Reference",
      dataIndex: "inputReference",
      key: "inputReference",
      render: (_: any, r: BenchmarkResult) => <ReferenceViewer result={r} />,
    },
    {
      title: "Actual Output Preview",
      key: "actualOutput",
      render: (_: any, r: BenchmarkResult) => (
        <Text
          className="text-xs block max-w-[300px] text-gray-600"
          ellipsis={{ tooltip: formatOutput(r.actualOutput) }}
        >
          {formatOutput(r.actualOutput)}
        </Text>
      ),
    },
    {
      title: "Judge Score",
      dataIndex: "qualityScore",
      key: "qualityScore",
      sorter: (a: BenchmarkResult, b: BenchmarkResult) =>
        (a.qualityScore ?? -1) - (b.qualityScore ?? -1),
      render: (score: number | null) => {
        if (score == null) return <span className="text-gray-300">—</span>;
        const color = score >= 8 ? "success" : score >= 5 ? "warning" : "error";
        return <Tag color={color}>{score}/10</Tag>;
      },
    },
  ];

  const handleConfirm = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("No items selected for promotion.");
      return;
    }
    promoteMutation.mutate(selectedRowKeys as string[]);
  };

  return (
    <Modal
      title="Promote Results to Ground Truth"
      open={isOpen}
      onCancel={onClose}
      width={800}
      footer={[
        <div key="actions" className="flex justify-between w-full">
          <Popconfirm
            title="Clear Ground Truth?"
            description="This will permanently delete the expected.json file for this benchmark configuration."
            onConfirm={() => deleteMutation.mutate()}
            okText="Yes, delete it"
            cancelText="No"
            placement="topLeft"
          >
            <Button danger loading={deleteMutation.isPending}>
              Clear Ground Truth
            </Button>
          </Popconfirm>
          <div className="flex gap-2">
            <Button
              key="cancel"
              onClick={onClose}
              disabled={promoteMutation.isPending || deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              className="bg-amber-600 hover:bg-amber-500 border-amber-600"
              loading={promoteMutation.isPending}
              onClick={handleConfirm}
              disabled={selectedRowKeys.length === 0}
            >
              Confirm Promotion ({selectedRowKeys.length} items)
            </Button>
          </div>
        </div>,
      ]}
    >
      <div className="mb-4 text-gray-600">
        Select the outputs you wish to permanently save as expected ground truth
        for future regressions. Outputs with a Judge score of 8 or higher have
        been pre-selected.
      </div>
      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          getCheckboxProps: (record) => ({
            disabled: !!(
              record.actualOutput && (record.actualOutput as any).error
            ),
          }),
        }}
        columns={columns as any}
        dataSource={results}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};
